import { ButtonHTMLAttributes, ForwardedRef, forwardRef } from 'react';

import { IconArrowDown, IconArrowUp, IconCircleX, IconCirclePlus } from '@tabler/icons-react';
import classnames from 'classnames';

export type PillButtonType = 'primary' | 'muted';

export interface PillButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  description?: string;
  children?: React.ReactNode;
  iconName?: string;
  endEnhancer?: React.ReactNode;
  value?: string;
  isActive?: boolean;
  isFocused?: boolean;
  iconOnClick?: () => void;
  pillBtnType?: PillButtonType;
  showIconAlways?: boolean;
}

export const PillButton = forwardRef(
  (
    {
      description,
      children,
      value,
      iconName,
      endEnhancer,
      className,
      iconOnClick,
      isActive,
      isFocused,
      pillBtnType = 'primary',
      showIconAlways,
      onClick,
      ...buttonProps
    }: PillButtonProps,
    ref: ForwardedRef<HTMLButtonElement>,
  ) => {
    const displayText = description || children;
    const hasIcon = !!iconName || !!endEnhancer;
    const isClickable = !!onClick;
    const isPrimary = pillBtnType === 'primary' || !pillBtnType;
    const alwaysShowIcon = showIconAlways ?? false;

    const baseClasses =
      'rounded-full py-[6px] px-4 h-[30px] flex items-center mr-[18px] whitespace-nowrap transition-all duration-500 ease-in-out';
    const clickableClass = isClickable ? 'cursor-pointer' : '';

    const primaryVariantClasses = isPrimary
      ? classnames('bg-gray-950 border-[0.5px] border-gray-800', {
          'active:border-pink-500 active:border active:bg-slate-950': true,
          'border-pink-500 border bg-slate-950': isActive,
        })
      : '';

    const mutedVariantClasses =
      pillBtnType === 'muted' ? 'bg-slate-700 border border-slate-600' : '';

    const descClasses = isPrimary ? (value ? 'text-gray-500' : 'text-white') : 'text-slate-100';

    const iconContainerClasses = hasIcon
      ? alwaysShowIcon
        ? classnames('flex items-center pl-2 pointer-events-auto')
        : classnames(
            'flex items-center',
            'opacity-0 max-w-0 overflow-hidden pl-0',
            'transition-all duration-500 ease-in-out',
            'pointer-events-auto',
            'group-hover:opacity-100 group-hover:max-w-[50px] group-hover:pl-2',
            'group-focus:opacity-100 group-focus:max-w-[50px] group-focus:pl-2',
            {
              'opacity-100 max-w-[50px] pl-2': isFocused,
            },
          )
      : '';

    const iconPaddingClasses = hasIcon
      ? alwaysShowIcon
        ? 'pr-[6px]'
        : classnames('group-hover:pr-[6px] group-focus:pr-[6px]', {
            'pr-[6px]': isFocused,
          })
      : '';

    const buttonClassName = classnames(
      baseClasses,
      clickableClass,
      primaryVariantClasses,
      mutedVariantClasses,
      iconPaddingClasses,
      'group',
      className,
    );

    let icon;

    if (endEnhancer) {
      icon = endEnhancer;
    } else {
      switch (iconName) {
        case 'pill-close':
          icon = <IconCircleX color="white" size={18} />;
          break;
        case 'arrow-up':
          icon = <IconArrowUp color="white" size={18} />;
          break;
        case 'arrow-down':
          icon = <IconArrowDown color="white" size={18} />;
          break;
        case 'pill-plus':
          // Matches pill-close's IconCircleX (circle-outlined) — was a bare
          // IconPlus with no circle, so add/remove read as two different
          // icon languages on otherwise-identical pills.
          icon = <IconCirclePlus color="white" size={18} />;
          break;
      }
    }

    const iconWrapperClasses = classnames(iconContainerClasses, {
      '[&>svg]:fill-white': pillBtnType === 'muted',
    });

    return (
      <button {...buttonProps} ref={ref} onClick={onClick} className={buttonClassName}>
        <span className={descClasses}>{displayText}</span>
        {value && <span className="pl-2 font-pt-mono text-gray-200">{value}</span>}
        {hasIcon ? (
          <span
            className={iconWrapperClasses}
            onClick={(e) => {
              e.stopPropagation();
              if (iconOnClick) {
                e.preventDefault();
                iconOnClick();
              } else if (onClick) {
                onClick(e as any);
              }
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {icon}
          </span>
        ) : null}
      </button>
    );
  },
);
